/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import type { LoanApplyMarketReqType } from '@/api/apply-product/types';
import { EquityPathType } from '@/api/apply-product/types';
import { getRightsSummary } from '@/api/rights';

import {
  RightPackageSourceEnum, RightsSummaryGet, RightSummaryType,
} from '@/api/rights/types';
import {
  ubtButtonNew, ubtModuleExposeNew,
} from '@/shared/utils/ubt';
import store from '@/store';
import { LoanRightStateType } from '@/views_v2/apply-product/types';
import { Toast, Dialog } from '@hb/coffee';
import { PricingTypeEnum } from '@/enums/rights';
import ProductCommodity from '@/shared/commodity';
import { RightTipsDialogActionEnum, RightTipsDialogSceneEnum, RightTipsDialogUbtEnum } from './enum';

export const RightDataDialog = {
  /** 是否展示了权益概要数据查询错误弹窗 */
  showSummaryDataTips: false,

  /** 埋点数据上报通用封装 */
  sendUbtData(params: Dictionary, type?: RightTipsDialogUbtEnum):void {
    const pageId = '臻有钱_提现申请页';
    const moduleId = 'finance_loan_withdrawal_rightsTipDialog';
    if (type === RightTipsDialogUbtEnum.EXPOSE) {
      ubtModuleExposeNew(
        pageId,
        moduleId,
        params,
      );
    } else {
      ubtButtonNew(
        pageId,
        moduleId,
        params,
      );
    }
  },

  closeDialog: ():void => {
    if (RightDataDialog.showSummaryDataTips) {
      Dialog.close();
      RightDataDialog.showSummaryDataTips = false;
    }
  },

  /** 扩展封装，权益接口数据返回异常或者提现接口返回权益错误相关的code，执行callback:刷新页面的操作 */
  showDialog: (
    scene: RightTipsDialogSceneEnum,
    action: RightTipsDialogActionEnum,
    callback?: ()=>void,
  ):void => {
    // TODO:埋点定义封装
    RightDataDialog.showSummaryDataTips = true;
    RightDataDialog.sendUbtData({ scene }, RightTipsDialogUbtEnum.EXPOSE);
    Dialog.alert({
      title: '温馨提示',
      message: '由于网络异常页面无法正常展示，请点击刷新按钮或退出页面重新进入页面进行尝试',
      confirmButtonText: '刷新',
      closeable: false,
    }).then(() => {
      RightDataDialog.sendUbtData({ scene, action });
      // 增加点击埋点
      RightDataDialog.showSummaryDataTips = false;

      if (callback) {
        callback();
      }
    });
  },
};

/** 是否为当前提现金额 */
const isCurrentAmount = (
  requestAmount: number, currentAmount: string,
) => Number(requestAmount) === Number(currentAmount);
/** 是否为当前提现期数 */
const isCurrentPeriod = (
  requestPeriod: number, currentPeriod: number | string,
) => Number(requestPeriod) === Number(currentPeriod);
/** 是否为当前提现产品 */
const isCurrentProductId = (
  requestProductId: string, currentProductId: string,
) => String(requestProductId) === String(currentProductId);

/** 是否为当前提现资方类型 */
const isCurrentCapitalCode = (
  requestCapitalCode: string, currentCapitalCode: string,
) => String(requestCapitalCode) === String(currentCapitalCode);

/** 是否为当前页面稳定时的接口返回值 */
export const isLastRequsetSummaryData = (
  rightSummaryRes: RightsSummaryGet,
):boolean => {
  const paramsNow = store.state.applyProduct;

  const {
    orderAmount, period, productId, capitalCode,
  } = rightSummaryRes;
  const { channel: currentChannel, amount: currentAmount, period: currentPeriod } = paramsNow;
  const { capitalCode: currentCapitalCode } = currentChannel || {};

  const validataBaseChannelInfoRes = !!((
    orderAmount && isCurrentAmount(orderAmount, currentAmount))
    && (period
      && currentPeriod
      && isCurrentPeriod(period, currentPeriod && currentPeriod.value))
    && (productId
      && currentChannel
      && isCurrentProductId(productId, currentChannel && currentChannel.productId)));

  const validataCapitalCodeRes = !!(capitalCode && currentCapitalCode
    && isCurrentCapitalCode(capitalCode, currentCapitalCode));

  if (productId === ProductCommodity.AUTARKY_B_COMMODITY) {
    return !!(validataBaseChannelInfoRes && validataCapitalCodeRes);
  }
  return validataBaseChannelInfoRes;
};

/** 对权益查询接口的统一封装
 * 返回权益查询接口，如果发现异常，且异常数据为最后一次请求，抛出该异常信息并且让用户尝试重新刷新一次
*/
export const getSummaryDataHandler = async (
  params: RightsSummaryGet,
): Promise<RightSummaryType> => {
  try {
    return await getRightsSummary(params);
  } catch (err) {
    // 如果是最后一次请求接口发生异常，抛出异常错误，中间接口的问题不用处理，避免重复出现提示
    if (isLastRequsetSummaryData(params)) {
      RightDataDialog.showDialog(
        RightTipsDialogSceneEnum.NET_ERROR,
        RightTipsDialogActionEnum.REFRESH_RIGHTS_DATA,
        () => {
          // 点击刷新按钮后重新请求一次数据
          Toast.loading({
            duration: 0,
            forbidClick: true,
            message: '信息获取中，请耐心等待',
          });
          // 调用store中的刷新方法
          store.dispatch('loanRight/getRightSummaryData');
        },
      );
    }

    return Promise.reject(err);
  }
};

/** 根据用户提现资方类型，结合用户操作来组装提现参数 */
export const formatZhenXiangCardParams = (
  rightParams: LoanRightStateType['rightParams'],
  isCountingDownFinished: LoanRightStateType['isCountingDownFinished'],
  equityValue: EquityPathType,
  /** 新增定价类型参数，用于参数传递 */
  pricingType?: PricingTypeEnum,
): LoanApplyMarketReqType => {
  const { packageType } = rightParams || {};
  let formatRightParams = {
    equityPath: equityValue,
    pricingType,
    /** 增加支持下单的packageType参数，用于提现订单落库，这样确保能回溯数据 */
    packageType,
  };
  // 如果是臻享卡购买用户
  const {
    packageId = '', sellPrice, originalPrice,
  } = rightParams || {};
  const rightPackageInfo = {
    packageFrom: RightPackageSourceEnum.LOAN,
    packageId,
    expectPrice: isCountingDownFinished ? originalPrice : sellPrice,
    packageType,
  };

  if (equityValue === EquityPathType.BUY_RIGHTS) {
    formatRightParams = {
      ...rightPackageInfo,
      ...formatRightParams,
    };
  }

  return formatRightParams;
};
